import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 50. SETTINGS
// Système de paramètres à plusieurs niveaux, du plus général au plus précis :
//
//   Platform → Country → Business → Branch → User
//
// Exemples : langue, devise, fuseau horaire, notifications, paramètres de
// facturation, préférences d'affichage. Les niveaux plus précis écrasent les
// niveaux plus généraux. Les modules résolvent leurs paramètres via
// resolveSettings() sans se soucier de la cascade.
// ─────────────────────────────────────────────────────────────

export type SettingLevel = 'platform' | 'country' | 'business' | 'branch' | 'user';

export const SETTING_LEVELS: { key: SettingLevel; label: string; icon: string; desc: string; className: string }[] = [
  { key: 'platform', label: 'Plateforme', icon: 'ri-earth-line', desc: 'Valeurs par défaut de toute la plateforme', className: 'bg-foreground-100 text-foreground-700' },
  { key: 'country', label: 'Pays', icon: 'ri-map-2-line', desc: 'Surcharge par pays (devise, langue, locale)', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'business', label: 'Entreprise', icon: 'ri-building-2-line', desc: 'Réglages propres à votre entreprise', className: 'bg-primary-100 text-primary-900' },
  { key: 'branch', label: 'Établissement', icon: 'ri-store-2-line', desc: 'Réglages d’un établissement précis', className: 'bg-accent-100 text-accent-900' },
  { key: 'user', label: 'Utilisateur', icon: 'ri-user-line', desc: 'Préférences personnelles de l’utilisateur', className: 'bg-background-100 text-foreground-700' },
];

export function settingLevelMeta(key: string | null | undefined) {
  return SETTING_LEVELS.find((l) => l.key === key) || SETTING_LEVELS[0];
}

export type SettingValueType = 'text' | 'boolean' | 'number' | 'select';

export interface SettingDef {
  key: string;
  label: string;
  group: string;
  icon: string;
  type: SettingValueType;
  options?: { value: string; label: string }[];
  defaultValue: string | number | boolean;
  desc?: string;
}

/** Définitions des paramètres résolubles (cascade multi-niveaux). */
export const SETTING_DEFS: SettingDef[] = [
  { key: 'language', label: 'Langue', group: 'Général', icon: 'ri-translate-2', type: 'select', defaultValue: 'fr', options: [
    { value: 'fr', label: 'Français' }, { value: 'en', label: 'English' }, { value: 'ar', label: 'العربية' }, { value: 'es', label: 'Español' },
  ] },
  { key: 'currency', label: 'Devise', group: 'Général', icon: 'ri-money-dollar-circle-line', type: 'select', defaultValue: 'MAD', options: [
    { value: 'MAD', label: 'MAD — Dirham' }, { value: 'EUR', label: 'EUR — Euro' }, { value: 'USD', label: 'USD — Dollar' }, { value: 'XOF', label: 'XOF — Franc CFA' },
  ] },
  { key: 'timezone', label: 'Fuseau horaire', group: 'Général', icon: 'ri-global-line', type: 'text', defaultValue: 'Africa/Casablanca' },
  { key: 'notifications_email', label: 'Notifications par email', group: 'Notifications', icon: 'ri-mail-line', type: 'boolean', defaultValue: true },
  { key: 'notifications_push', label: 'Notifications push', group: 'Notifications', icon: 'ri-notification-3-line', type: 'boolean', defaultValue: false },
  { key: 'notifications_sms', label: 'Notifications SMS', group: 'Notifications', icon: 'ri-message-3-line', type: 'boolean', defaultValue: false },
  { key: 'billing_terms_days', label: 'Délai de paiement (jours)', group: 'Facturation', icon: 'ri-calendar-schedule-line', type: 'number', defaultValue: 30 },
  { key: 'billing_vat_rate', label: 'Taux de TVA par défaut (%)', group: 'Facturation', icon: 'ri-percent-line', type: 'number', defaultValue: 20 },
  { key: 'billing_footer', label: 'Mention pied de facture', group: 'Facturation', icon: 'ri-file-text-line', type: 'text', defaultValue: '' },
  { key: 'display_density', label: 'Densité d’affichage', group: 'Affichage', icon: 'ri-layout-line', type: 'select', defaultValue: 'comfortable', options: [
    { value: 'compact', label: 'Compacte' }, { value: 'comfortable', label: 'Confortable' }, { value: 'large', label: 'Large' },
  ] },
  { key: 'display_theme', label: 'Thème', group: 'Affichage', icon: 'ri-palette-line', type: 'select', defaultValue: 'system', options: [
    { value: 'light', label: 'Clair' }, { value: 'dark', label: 'Sombre' }, { value: 'system', label: 'Système' },
  ] },
];

export function settingDefMeta(key: string | null | undefined): SettingDef | undefined {
  return SETTING_DEFS.find((d) => d.key === key);
}

export interface CoreSetting {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  level: string;
  scope_ref: string | null;
  location_id: number | null;
  user_id: number | null;
  setting_key: string;
  value: unknown;
  created_at: string | null;
  updated_at: string | null;
}

export interface SetSettingInput {
  tenantId?: number | null;
  organizationId?: number | null;
  level: SettingLevel;
  settingKey: string;
  value: unknown;
  /** Référence de périmètre : code pays, id établissement, id utilisateur… */
  scopeRef?: string | null;
  locationId?: number | null;
  userId?: number | null;
}

export async function setSetting(input: SetSettingInput): Promise<void> {
  const scopeRef = input.scopeRef ?? null;
  const { data: existing } = await supabase
    .from('core_settings')
    .select('id')
    .eq('level', input.level)
    .eq('setting_key', input.settingKey)
    .is('scope_ref', scopeRef)
    .maybeSingle();

  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId ?? null,
    level: input.level,
    scope_ref: scopeRef,
    location_id: input.locationId ?? null,
    user_id: input.userId ?? null,
    setting_key: input.settingKey,
    value: input.value,
    updated_at: new Date().toISOString(),
  };

  if (existing?.id) {
    const { error } = await supabase.from('core_settings').update(payload).eq('id', existing.id as number);
    if (error) throw error;
    return;
  }
  const { error } = await supabase.from('core_settings').insert(payload);
  if (error) throw error;
}

export async function listSettings(filter?: {
  organizationId?: number | null;
  level?: SettingLevel;
}): Promise<CoreSetting[]> {
  let query = supabase.from('core_settings').select('*').order('level', { ascending: true }).order('setting_key', { ascending: true });
  if (filter?.level) query = query.eq('level', filter.level);
  if (filter?.organizationId != null) {
    query = query.or(`organization_id.is.null,organization_id.eq.${filter.organizationId}`);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreSetting[];
}

export interface ResolveSettingsInput {
  organizationId?: number | null;
  tenantId?: number | null;
  country?: string | null;
  locationId?: number | null;
  userId?: number | null;
}

/**
 * Résout les paramètres effectifs en appliquant la cascade
 * Platform → Country → Business → Branch → User. Le niveau le plus précis gagne.
 * Renvoie une map { setting_key: valeur }, avec les valeurs par défaut en repli.
 */
export async function resolveSettings(input: ResolveSettingsInput): Promise<Record<string, unknown>> {
  const rows = await listSettings({ organizationId: input.organizationId ?? null });

  // Valeurs par défaut.
  const resolved: Record<string, unknown> = {};
  SETTING_DEFS.forEach((d) => { resolved[d.key] = d.defaultValue; });

  const matches = (row: CoreSetting, level: SettingLevel): boolean => {
    if (row.level !== level) return false;
    switch (level) {
      case 'platform': return row.organization_id == null && !row.scope_ref;
      case 'country': return !!input.country && row.scope_ref === input.country;
      case 'business': return input.organizationId != null && row.organization_id === input.organizationId && row.location_id == null && row.user_id == null;
      case 'branch': return input.locationId != null && row.location_id === input.locationId;
      case 'user': return input.userId != null && row.user_id === input.userId;
      default: return false;
    }
  };

  // Cascade : du plus général au plus précis.
  (['platform', 'country', 'business', 'branch', 'user'] as SettingLevel[]).forEach((level) => {
    rows.filter((r) => matches(r, level)).forEach((r) => {
      resolved[r.setting_key] = r.value;
    });
  });

  return resolved;
}

export interface SettingStats {
  platform: number;
  country: number;
  business: number;
  branch: number;
  user: number;
  total: number;
}

export async function fetchSettingStats(organizationId?: number | null): Promise<SettingStats> {
  const rows = await listSettings({ organizationId: organizationId ?? null });
  const stats: SettingStats = { platform: 0, country: 0, business: 0, branch: 0, user: 0, total: rows.length };
  rows.forEach((r) => {
    const lvl = r.level as SettingLevel;
    if (lvl in stats) stats[lvl] += 1;
  });
  return stats;
}

export function coerceSettingValue(def: SettingDef | undefined, raw: string | boolean | number): unknown {
  if (!def) return raw;
  if (def.type === 'boolean') return typeof raw === 'boolean' ? raw : raw === 'true' || raw === '1';
  if (def.type === 'number') return typeof raw === 'number' ? raw : Number(raw) || 0;
  return String(raw);
}